<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="main">
    <div class="left-container">
      <div class="filter-table-controls">
        <div class="filter-summary-container">
          <span>{{ filterSummary }}</span>
        </div>
        <ButtonFilter buttonText="Change Filter" filterPosition="right" :filterWidth="500" :isOpen.sync="filterIsOpen">
          <div class="filter-content">
            <BoxContainer
              title="PO Date"
              :isCheckBox="true"
              :isChecked.sync="poDateFilterIsChecked"
              style="display: flex; flex-direction: row; gap: 8px"
            >
              <DateTimePicker
                placeholder="Start Date"
                :isRequired="true"
                :value.sync="startPODateValue"
                :errorMessage.sync="startPODateValueError"
                :isDisabled="!poDateFilterIsChecked"
                :showTime="false"
              />
              <DateTimePicker
                placeholder="End Date"
                :value.sync="endPODateValue"
                :isDisabled="!poDateFilterIsChecked"
                :showTime="false"
              />
            </BoxContainer>
            <div class="refresh-button">
              <button @click="refreshFilter">Refresh</button>
            </div>
          </div>
        </ButtonFilter>
      </div>
      <TableBox
        :tableData="purchaseOrderListFiltered"
        :selectedRow.sync="purchaseOrderTableSelectedRows"
        title="Purchase Order"
        :loading="purchaseOrderTableLoading"
        :maxSelection="-1"
        :showSelectAll="true"
        style="min-height: 0"
        :dataType="purchaseOrderListDataType"
      />
      <div style="display: flex; justify-content: end; width: 100%; margin-top: 10px">
        <Button style="width: fit-content" @click="previewClicked">Preview Purchase Order</Button></div
      >
    </div>
    <div class="right-container"
      ><DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        ref="designerV3"
        @shareClicked="designerV3ShareClicked"
        @afterPrint="designerV3AfterPrint"
      />
      <div class="user-instructions" v-else>
        <span
          >Select some Purchase Order and click on <br /><strong>Preview Purchase Order</strong> to get started</span
        >
      </div></div
    >

    <PopupV2
      class="email-popup"
      :show="showEmailPopup"
      icon="https://dsmlib.logistics.systems/Icons/BlackMail.png"
      :buttons="emailPopupButtons"
      title="Email Work Order"
      width="60%"
      @buttonClicked="emailPopupButtonClicked"
    >
      <div class="email-popup-content-container">
        <div class="mail-inputs-container">
          <div class="email-input-container">
            <EmailInput :mails.sync="emailPopupToMails" title="To" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailPopupCCMails" title="CC" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailPopupBCCMails" title="BCC" />
          </div>
        </div>
        <div class="email-content-inputs-container">
          <Input :inputValue.sync="emailPopupSubjectInputValue" placeholder="Subject" validator="text,100" />
          <RemarksBox title="Message" :text.sync="emailPopupMessageText" :maxLength="1000" />
        </div>
      </div>
    </PopupV2>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    filterSummary() {
      if (this.filterActive && this.startPODateValue && this.endPODateValue) {
        return `PO Date: ${this.startPODateValue.formatString('yyyy-MM-dd HH:mm')} to ${this.endPODateValue.formatString('yyyy-MM-dd HH:mm')}`;
      }
      return 'No filter applied!';
    },

    stateItem() {
      return this.$store.state;
    },
  },
  data: function () {
    return {
      filterIsOpen: false,

      filterActive: false,

      filterPONo: null,

      poDateFilterIsChecked: false,
      startPODateValue: null,
      endPODateValue: null,
      startPODateValueSaved: null,
      endPODateValueSaved: null,

      startPODateValueError: '',

      purchaseOrderList: [],
      purchaseOrderTableSelectedRows: [],
      purchaseOrderTableLoading: true,

      purchaseOrderListFiltered: [],

      statusSet: {
        N: 'New',
        P: 'Printed',
        C: 'Cancelled',
        S: 'Send',
        H: 'HQ read PO/SR',
      },

      purchaseOrderListDataType: {
        '#': 'number',
        'PO #': 'string',
        'Ref #': 'string',
        'Purchase Date': 'date',
        Supplier: 'string',
        Status: 'string',
        'Job Type': 'string',
        'Total Value': 'currency',
        'Date Updated': 'datetime',
        'Updated By': 'string',
      },

      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: [],
      designerV3ShareBehaviour: {
        behaviour: 'uploadAndEmit',
        options: {
          fileName: '',
          folder: '',
        },
      },

      showEmailPopup: false,
      emailPopupButtons: [{ text: 'Send Email' }, { text: 'Cancel', color: 'lightgray' }],
      emailPopupToMails: [],
      emailPopupCCMails: [],
      emailPopupBCCMails: [],
      emailPopupSubjectInputValue: '',
      emailPopupMessageText: '',

      employeeList: [],

      localCurrency: 'RM',
    };
  },
  props: {},
  methods: {
    async getPurchaseOrder() {
      this.purchaseOrderTableLoading = true;
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PURCHASEORDER' });
      this.purchaseOrderList = [];
      this.$nextTick(() => {
        this.purchaseOrderList = result;
      });
      this.purchaseOrderTableLoading = false;
    },

    refreshFilter() {
      if (!this.poDateFilterIsChecked) {
        this.filterActive = false;
        this.startPODateValue = null;
        this.endPODateValue = null;
        this.filterIsOpen = false;
        return;
      }

      this.filterPONo = null;

      this.startPODateValueError = '';

      if (!this.startPODateValue) {
        this.startPODateValueError = 'Start Date is required';
        return;
      }

      if (!this.endPODateValue) {
        this.endPODateValue = new Date();
      }

      this.startPODateValueSaved = this.startPODateValue;
      this.endPODateValueSaved = this.endPODateValue;

      this.filterActive = true;
      this.filterIsOpen = false;

      this.purchaseOrderTableSelectedRows = [];

      if (!this.filterActive && !this.filterPONo) return [];
      const reorder = this.purchaseOrderList
        .filter((item) => {
          const { PODate: podate } = item;
          const PODate = podate ? new Date(podate) : null;
          return PODate >= this.startPODateValueSaved && PODate <= this.endPODateValueSaved;
        })
        .map((item, index) => {
          const { PONO, PODate, Supplier, Spp_Name, Status, JobDescription, TotalValue, RefNo, dateUpdate, UserID } =
            item;
          return {
            '#': index + 1,
            'PO #': PONO,
            'Ref #': RefNo,
            'Purchase Date': PODate,
            Supplier: `${Supplier} - ${Spp_Name}`,
            'Job Type': JobDescription ?? '',
            'Total Value': TotalValue,
            Status: this.statusSet[Status] ? `${Status} (${this.statusSet[Status]})` : Status,
            'Date Updated': dateUpdate,
            'Updated By': UserID,
          };
        });

      this.purchaseOrderListFiltered = [];

      this.$nextTick(() => {
        this.purchaseOrderListFiltered = reorder;
      });
    },

    async previewClicked() {
      this.$fullscreenLoading('Getting Purchase Order...');

      const polist = this.purchaseOrderTableSelectedRows.map((item) => item['PO #']).join(',');

      let CompanyInfo;
      let PO;
      try {
        const [result, savedPOs] = await Promise.all([
          this.$apiCallV2('/PurchaseOrder/PrintPurchaseOrder', { polist }),
          this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PURCHASEORDER' }),
        ]);
        const printPO = result.PrintPO || {};
        CompanyInfo = printPO.CompanyInfo;
        PO = await Promise.all(this.convertToArray(printPO.PO).map(async (po) => {
          const savedPO = (savedPOs || []).find((item) => String(item.PONO).trim() == String(po.PONo).trim());
          if (!savedPO || savedPO.TotalValue == null || !Number.isFinite(Number(savedPO.TotalValue))) {
            throw new Error(`Unable to load saved total for ${po.PONo}`);
          }
          const details = await this.$apiCallV2('/Master/GetDetailsMaster', {
            masterfile: 'PURCHASEORDER', recordid: po.PONo,
          });
          if (!details || !details.PO || !details.PO.PODetail) {
            throw new Error(`Unable to load discount details for ${po.PONo}`);
          }
          const printDetails = this.convertToArray(po.PODetails);
          return {
            ...po,
            TotalValue: savedPO.TotalValue,
            PODetails: this.convertToArray(details.PO.PODetail).map((detail) => ({
              ...printDetails.find((item) => String(item.ProdCode).trim() == String(detail.ProdCode).trim()),
              ...detail,
              ProdName: detail.Name,
            })),
          };
        }));
      } catch (error) {
        this.showDesignerV3 = false;
        this.$showAlert('Unable to preview Purchase Order', error.message, [{ text: 'OK' }]);
        return;
      } finally {
        this.$fullscreenLoading();
      }
      const { addr1, addr2, addr3, addr4 } = CompanyInfo;

      const designerFiles = {
        general: {
          companyName: addr1 ?? '',
          companyAddress: [addr2, addr3, addr4].filter(Boolean).join('\n'),
          localCurr: this.localCurrency,
        },

        purchaseOrder: PO.map((po) => {
          const {
            Spp_Name,
            Spp_Addr1,
            Spp_Addr2,
            Spp_Addr3,
            Spp_Addr4,
            Spp_Post_Code,
            Spp_City,
            Spp_Country,
            Spp_TelNo,
            Spp_Fax,
            PONo,
            PODate,
            RefNo,
            Remark,
            PODetails,
            PODo,
            TotalValue,
          } = po;
          const addr5 = [Spp_Post_Code, Spp_City, Spp_Country].filter(Boolean).join(', ');

          const poDetails = this.convertToArray(PODetails);
          const poDO = PODo ? this.groupDO(this.convertToArray(PODo)) : [];

          const totalTaxAmount = poDetails.reduce(
            (acc, { Taxable_AmountGST2 }) => acc + (parseFloat(Taxable_AmountGST2) || 0),
            0,
          );
          const totalAfterTax = Number(TotalValue);
          const totalBeforeTax = totalAfterTax - totalTaxAmount;

          return {
            supplierName: Spp_Name,
            supplierAddress: [Spp_Addr1, Spp_Addr2, Spp_Addr3, Spp_Addr4, addr5].filter(Boolean).join('\n'),
            supplierPhone1: Spp_TelNo ? `Tel: ${Spp_TelNo}` : '',
            supplierPhone2: Spp_Fax ? `Fax: ${Spp_Fax}` : '',
            pageNo: '{{jsreportPageNum}} of {{jsreportTotalPage}}',
            poNo: PONo,
            date: new Date(PODate).formatString('dd/MM/yyyy HH:mm'),
            refNo: RefNo,
            remarksShow: Boolean(Remark),
            remarks: Remark,
            prodItem: poDetails.map((detail, index) => {
              const {
                ProdCode,
                ProdName,
                Quantity,
                Sub,
                PackageCode,
                subPackCode2,
                subQtyUnit3,
                UnitPrice,
                Discount,
                Taxable_AmountGST2,
                Remark,
              } = detail;

              // Calculate subText
              let subText = PackageCode ? `${Quantity} ${PackageCode}` : '';
              if (Sub === '1' && parseFloat(subQtyUnit3) > 0) {
                const cartonQty = Math.floor(parseFloat(Quantity) / parseFloat(subQtyUnit3)) || 0;
                const singleUnitQty = parseFloat(Quantity) % parseFloat(subQtyUnit3) || 0;
                subText = `(${cartonQty} ${subPackCode2} x ${subQtyUnit3}) + ${singleUnitQty} ${PackageCode}`;
              }

              // Assign calculated / mapped fields
              const prodQty = parseFloat(Quantity).toFixed(2);
              const prodUOM = PackageCode;
              const unitPrice = this.$currencyFormat(UnitPrice) || UnitPrice;
              const taxAmt = this.$currencyFormat(Taxable_AmountGST2 || 0);
              const gross = Number((parseFloat(UnitPrice) * parseFloat(Quantity)).toFixed(4));
              const discountAmount = Number(((gross * (parseFloat(Discount) || 0)) / 100).toFixed(4));
              const totalPrice = this.$currencyFormat(Number((gross - discountAmount).toFixed(4)));
              const prodRemarks = Remark || ''; // optional remark

              return {
                itemIndex: index + 1,
                prodCode: ProdCode,
                prodName: ProdName,
                prodAmountCalc: subText,
                prodRemarks: prodRemarks,
                prodQty,
                prodUOM,
                unitPrice,
                taxAmt,
                totalPrice,
              };
            }),

            doSingle: poDO.length == 1,
            doMultiple: poDO.length > 1,
            deliveryItem: poDO.map((po) => {
              const { Contact, PhoneNo1, CompanyName, Addr, items } = po;
              return {
                doMultiple: poDO.length > 1,
                contactPerson: Contact ?? '',
                doPhone1: PhoneNo1 ?? '',
                deliveryLocation: CompanyName ?? '',
                deliveryAddress: Addr ?? '',
                doProduct: items.map((prod, index) => {
                  const { PackageCode, ProdCode, ProdName, Qty, Sub, subPackCode2, subQtyUnit3 } = prod;
                  let subText = PackageCode ? `${Qty} ${PackageCode}` : '';
                  if (Sub === '1' && parseFloat(subQtyUnit3) > 0) {
                    const cartonQty = Math.floor(parseFloat(Qty) / parseFloat(subQtyUnit3)) || 0;
                    const singleUnitQty = parseFloat(Qty) % parseFloat(subQtyUnit3) || 0;
                    subText = `(${cartonQty} ${subPackCode2} x ${subQtyUnit3}) + ${singleUnitQty} ${PackageCode}`;
                  }
                  return {
                    itemIndex: index + 1,
                    prodCode: ProdCode,
                    prodName: ProdName,
                    prodAmountCalc: subText,
                    prodQty: parseFloat(Qty).toFixed(2),
                    prodUOM: PackageCode,
                  };
                }),
              };
            }),

            totalBeforeTax: this.$currencyFormat(totalBeforeTax) || '0.00',
            totalTaxAmount: this.$currencyFormat(totalTaxAmount) || '0.00',
            totalAfterTax: this.$currencyFormat(totalAfterTax) || '0.00',
          };
        }),
      };

      this.designerV3ReportFiles = Array.from(
        { length: designerFiles.purchaseOrder.length },
        () => 'PurchaseOrder.json',
      );
      this.designerV3ReportData = designerFiles.purchaseOrder;
      this.designerV3SharedData = designerFiles.general;
      this.showDesignerV3 = true;
    },

    designerV3ShareClicked(shareLink) {
      if (this.designerV3ReportData[0].email) {
        this.emailPopupToMails = this.designerV3ReportData[0].email.split(';').map((email) => ({
          email: email.trim(),
          valid: true,
        }));
      } else {
        this.emailPopupToMails = [];
      }
      this.emailPopupCCMails = [];
      this.emailPopupBCCMails = [];

      this.emailPopupSubjectInputValue = 'Purchase Order';
      this.emailPopupMessageText = `Purchase Order: ${shareLink}\n\n`;

      this.showEmailPopup = true;
    },

    async designerV3AfterPrint() {
      const requestBody = {
        report: 'PURCHASEORDER',
        poarray: this.designerV3ReportData.map((item) => item['poNo']).join(','),
      };
      const response = await this.$apiCallV2('/PurchaseOrder/UpdatePrintCount', requestBody);
      if (response !== '') {
        this.$showAlert(
          'error',
          'Fail to update print count',
          response?.ErrMsg.toString() ?? 'An error occured when trying to update print count',
          [{ text: 'OK' }],
        );
      }
    },

    async emailPopupButtonClicked(buttonText) {
      if (buttonText === 'Cancel') {
        this.showEmailPopup = false;
        return;
      }

      if (this.emailPopupToMails.filter((mail) => mail.valid).length === 0) {
        this.$showAlert('error', 'No Valid Email', "Please enter at least one valid 'To' email address", [
          { text: 'OK' },
        ]);
        return;
      }

      if (!this.emailPopupSubjectInputValue.trim()) {
        const proceedWithoutSubject = await this.$showAlert(
          'warning',
          'No Subject',
          'No subject was entered\nProceed to email without a subject?',
          [{ text: 'Proceed' }, { text: 'Cancel', color: 'lightgray' }],
        );

        if (proceedWithoutSubject === 'Cancel') return;
      }

      const requestBody = {
        emaillist: [
          {
            doctype: 'PURCHASEORDER',
            docno: this.designerV3ReportData[0]['poNo'],
            docnofieldname: '',
            recipient: this.emailPopupToMails.map((mail) => mail.email).join(';'),
            cc: this.emailPopupCCMails.map((mail) => mail.email).join(';'),
            bcc: this.emailPopupBCCMails.map((mail) => mail.email).join(';'),
            subject: this.emailPopupSubjectInputValue.trim(),
            message: this.emailPopupMessageText,
          },
        ],
      };

      this.$fullscreenLoading('Sending Email');
      const sendEmailResponse = await this.$apiCallV2('/PurchaseOrder/SendEmail', requestBody);
      this.$fullscreenLoading();

      if (sendEmailResponse?.trim?.()?.endsWith?.('queue to send.')) {
        this.$showAlert('success', 'Email successfully sent', 'Your email has been successfully sent', [
          { text: 'OK' },
        ]);
        this.showEmailPopup = false;
      } else {
        this.$showAlert('error', 'Email not sent', 'Your email could not be sent. Please try again.', [{ text: 'OK' }]);
      }
    },

    convertToArray(item) {
      if (!item) return [];
      return Array.isArray(item) ? item : [item];
    },

    async getEmployeeList() {
      this.employeeList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'EMPLOYEE' });
    },

    groupDO(poDoList) {
      const itemFields = ['ProdCode', 'ProdName', 'Qty', 'PackageCode', 'Sub', 'subPackCode2', 'subQtyUnit3'];

      const grouped = Object.values(
        poDoList.reduce((acc, item) => {
          // build unique key for grouping
          const key = [item.PONo, item.CompanyName, item.Contact, item.Addr, item.PhoneNo1].join('|');

          if (!acc[key]) {
            acc[key] = {
              PONo: item.PONo,
              CompanyName: item.CompanyName,
              Contact: item.Contact ?? '',
              Addr: item.Addr ?? '',
              PhoneNo1: item.PhoneNo1 ?? '',
              items: [],
            };
          }

          // push only item-related fields
          acc[key].items.push(Object.fromEntries(itemFields.map((f) => [f, item[f]])));

          return acc;
        }, {}),
      );

      return grouped;
    },

    async getLocalCurrency() {
      const companyDetails = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'COMPANY',
        module: 'MASTER',
        recordid: this.stateItem.loginDetails.CoCode,
      });
      const currString = companyDetails.Company.CompanyLocalCur;
      this.localCurrency = currString ?? 'RM';
    },
  },
  async mounted() {
    this.$fullscreenLoading('Getting Purchase Order...');
    await Promise.all([this.getEmployeeList(), this.getPurchaseOrder()]);

    this.$fullscreenLoading();

    const query = this.$route.query.pono?.trim() ?? null;
    if (query) {
      this.filterPONo = query;
      this.purchaseOrderListFiltered = [];
      const findPONo = this.purchaseOrderList.find((po) => po.PONO == query);
      this.$nextTick(() => {
        this.purchaseOrderTableSelectedRows = [{ 'PO #': query, _rowid: 'row0' }];
        this.purchaseOrderListFiltered = [findPONo];
      });
    }
  },
  watch: {
    stateItem() {
      if (this.stateItem) this.getLocalCurrency();
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/css/all.scss';
.main {
  padding: 10px 45px;
  display: flex;
  flex-direction: row;
  font-size: 12px;
  > .left-container {
    height: 100%;
    width: 50%;
    padding: 5px;
    border-radius: 5px;
    overflow: hidden;
    background-color: white;
    display: flex;
    flex-direction: column;
  }

  > .right-container {
    height: 100%;
    width: 50%;
    margin-left: 5px;
    border-radius: 5px;
    overflow: hidden;
    background-color: white;
    display: flex;
    flex-direction: column;

    .user-instructions {
      font-size: 1.25rem;
      padding: 3rem;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
    }
  }

  .filter-table-controls {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-bottom: 5px;

    > .filter-summary-container {
      margin-right: 5px;
      align-content: center;
      text-align: end;
    }

    .filter-content {
      text-align: left;
      cursor: auto;

      .refresh-button {
        margin-top: 10px;
        display: flex;
        justify-content: flex-end;
      }
    }

    > .select-deselect-all-button {
      margin-left: 5px;
    }
  }
  .email-popup {
    .email-popup-content-container {
      display: flex;
      flex-direction: row;

      > .mail-inputs-container {
        margin-right: 5px;
        display: flex;
        flex-direction: column;
        flex: 1;

        > .email-input-container {
          margin-bottom: 5px;
        }
      }

      > .email-content-inputs-container {
        margin-left: 5px;
        display: flex;
        flex-direction: column;
        flex: 2;
      }
    }
  }
}
</style>
